import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import { getAllOpposCounts } from 'redux/opportunities-service/action';
import {
	selectorGetChallengeOpportunitiesCount,
	selectorGetManualOpportunitiesCount,
	selectorGetPastOpportunitiesCount,
	selectorGetPrivateOpportunitiesCount,
	selectorGetTotalOpportunitiesCount,
	selectorGetUpcomingOpportunitiesCount,
} from 'redux/opportunities-service/selector';

import TabNav from 'shared/components/TabNav';
import TabNavItem from 'shared/components/TabNav/TabNavItem';
import { OppoContext, OPPORTUNITIES_TABS } from 'shared/components/OppListContent';

import styles from 'shared/components/OppListContent/OppListContent.module.scss';

import SuperAdminOpportunitiesTableNew from './SuperAdminOpportunitiesTableNew';
import { mapTabToOpportunityFilterType, parseTabFromSearch, SUPER_ADMIN_POSTS_TAB, toTabSearch } from './config/tabs';
import { useAdminPostsCount, useAdminPostsSearch } from './hooks/useAdminPostsSearch';

const SuperAdminOpportunitiesListContentNew: FC = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const history = useHistory();

	useAdminPostsSearch({ search: '', pageSize: 1 });

	const [activeTab, setActiveTab] = useState(() => parseTabFromSearch(location.search));

	const opportunitiesManualCount = useSelector(selectorGetManualOpportunitiesCount);
	const opportunitiesPastCount = useSelector(selectorGetPastOpportunitiesCount);
	const opportunitiesChallengeCount = useSelector(selectorGetChallengeOpportunitiesCount);
	const opportunitiesTotalCount = useSelector(selectorGetTotalOpportunitiesCount);
	const opportunitiesUpcomingCount = useSelector(selectorGetUpcomingOpportunitiesCount);
	const opportunitiesPrivateCount: number = useSelector(selectorGetPrivateOpportunitiesCount);
	const postsCount = useAdminPostsCount();

	const [isClearSearch, setIsClearSearch] = useState(false);

	// Keep tab counters consistent with the legacy screen.
	useEffect(() => {
		dispatch(getAllOpposCounts());
	}, [dispatch]);

	const opportunityFilterType = useMemo(() => mapTabToOpportunityFilterType(activeTab), [activeTab]);

	const generalTabClickHandler = async () => {
		setIsClearSearch(true);
		history.push({ search: toTabSearch(activeTab) });
	};

	useEffect(() => {
		generalTabClickHandler();
	}, [activeTab]);

	useEffect(() => {
		return () => {
			history.push({ search: '' });
		};
	}, []);

	return (
		<div className={styles.container}>
			<TabNav>
				<TabNavItem
					counter={opportunitiesTotalCount || 0}
					title="All"
					activeTab={activeTab === OPPORTUNITIES_TABS.ALL}
					clickHandler={() => setActiveTab(OPPORTUNITIES_TABS.ALL)}
				/>
				<TabNavItem
					counter={opportunitiesManualCount || 0}
					title="Pending Approval"
					activeTab={activeTab === OPPORTUNITIES_TABS.MANUAL}
					clickHandler={() => setActiveTab(OPPORTUNITIES_TABS.MANUAL)}
				/>
				<TabNavItem
					counter={opportunitiesPrivateCount || 0}
					title="Private"
					activeTab={activeTab === 'PRIVATE'}
					clickHandler={() => setActiveTab('PRIVATE')}
				/>
				<TabNavItem
					counter={opportunitiesChallengeCount || 0}
					title="Challenge"
					activeTab={activeTab === OPPORTUNITIES_TABS.CHALLENGE}
					clickHandler={() => setActiveTab(OPPORTUNITIES_TABS.CHALLENGE)}
				/>
				<TabNavItem
					counter={opportunitiesPastCount || 0}
					title="Past"
					activeTab={activeTab === OPPORTUNITIES_TABS.PAST}
					clickHandler={() => setActiveTab(OPPORTUNITIES_TABS.PAST)}
				/>
				<TabNavItem
					counter={opportunitiesUpcomingCount || 0}
					title="Upcoming"
					activeTab={activeTab === OPPORTUNITIES_TABS.UPCOMING}
					clickHandler={() => setActiveTab(OPPORTUNITIES_TABS.UPCOMING)}
				/>
				<TabNavItem
					counter={postsCount || 0}
					title="Posts"
					activeTab={activeTab === SUPER_ADMIN_POSTS_TAB}
					clickHandler={() => setActiveTab(SUPER_ADMIN_POSTS_TAB)}
				/>
			</TabNav>

			<OppoContext.Provider value={{ isClearSearch, setIsClearSearch }}>
				<SuperAdminOpportunitiesTableNew opportunityFilterType={opportunityFilterType} />
			</OppoContext.Provider>
		</div>
	);
};

export default SuperAdminOpportunitiesListContentNew;
